/**
 * Add watermark to an image
 * @param {string} imageUrl - URL of the image to watermark
 * @param {string} watermarkText - Text to add as watermark
 * @returns {Promise<Blob>} - Watermarked image as blob
 */
export const addWatermark = async (imageUrl, watermarkText = 'FITTTY') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // Only set crossOrigin for external URLs
    if (!imageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw original image
        ctx.drawImage(img, 0, 0);
        
        // Add semi-transparent overlay at bottom
        const overlayHeight = 50;
        const gradient = ctx.createLinearGradient(0, canvas.height - overlayHeight, 0, canvas.height);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, canvas.height - overlayHeight, canvas.width, overlayHeight);
        
        // Add watermark text
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(watermarkText, canvas.width / 2, canvas.height - overlayHeight / 2);
        
        // Convert to blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Image load error:', error);
      reject(new Error('Failed to load image for watermarking'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Download image with watermark
 * @param {string} imageUrl - URL of the image
 * @param {string} filename - Name for the downloaded file
 */
export const downloadImageWithWatermark = async (imageUrl, filename = 'fittty-tryon.jpg') => {
  try {
    const watermarkedBlob = await addWatermark(imageUrl);
    const url = URL.createObjectURL(watermarkedBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading image:', error);
    return false;
  }
};

/**
 * Share to Instagram (opens Instagram with image)
 * On mobile: Opens Instagram app
 * On desktop: Downloads image with instructions
 */
export const shareToInstagram = async (imageUrl) => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  try {
    const watermarkedBlob = await addWatermark(imageUrl);
    const file = new File([watermarkedBlob], 'fittty-tryon.jpg', { type: 'image/jpeg' });
    
    // Only try Web Share API on mobile devices
    if (isMobile && navigator.share && navigator.canShare) {
      // Check if can share files
      try {
        const canShareFiles = navigator.canShare({ files: [file] });
        
        if (canShareFiles) {
          await navigator.share({
            files: [file],
            title: 'My Virtual Try-On',
            text: 'Check out my virtual try-on from FITTTY! 👔✨'
          });
          return { success: true, method: 'share-api', message: 'Shared successfully!' };
        }
      } catch (shareError) {
        // User cancelled or share failed
        if (shareError.name === 'AbortError') {
          return { success: false, error: 'Share cancelled', cancelled: true };
        }
        console.warn('Share API failed, falling back to download:', shareError);
        // Fall through to download
      }
    }
    
    // Desktop or fallback: Download with instructions
    const url = URL.createObjectURL(watermarkedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fittty-instagram.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Small delay before revoking
    setTimeout(() => URL.revokeObjectURL(url), 100);
    
    return { 
      success: true, 
      method: 'download',
      message: isMobile 
        ? '✅ Image saved to gallery! Open Instagram and upload it.' 
        : '✅ Image downloaded! Check your Downloads folder and upload to Instagram.'
    };
  } catch (error) {
    console.error('Error sharing to Instagram:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to process image',
      details: 'Please try downloading the image manually'
    };
  }
};

/**
 * Share to TikTok
 */
export const shareToTikTok = async (imageUrl) => {
  try {
    const watermarkedBlob = await addWatermark(imageUrl);
    const file = new File([watermarkedBlob], 'fittty-tryon.jpg', { type: 'image/jpeg' });
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Try Web Share API first (works on modern mobile browsers)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'My Virtual Try-On',
          text: 'Virtual try-on powered by FITTTY! 🎨👕 #VirtualTryOn #Fashion #AI'
        });
        return { success: true, method: 'share-api' };
      } catch (shareError) {
        // User cancelled or share failed
        if (shareError.name === 'AbortError') {
          return { success: false, error: 'Share cancelled' };
        }
        // Fall through to download
      }
    }
    
    // Fallback: Download with instructions
    const url = URL.createObjectURL(watermarkedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fittty-tiktok.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return { 
      success: true, 
      method: 'download',
      message: isMobile 
        ? 'Image downloaded! Open TikTok and upload it from your gallery.'
        : 'Image downloaded! Open TikTok on your phone and upload it from your downloads.'
    };
  } catch (error) {
    console.error('Error sharing to TikTok:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Copy share link to clipboard
 */
export const copyShareLink = (imageUrl) => {
  const shareText = `Check out my virtual try-on from FITTTY! 🎨👔\n${window.location.origin}`;
  
  if (navigator.clipboard) {
    navigator.clipboard.writeText(shareText)
      .then(() => true)
      .catch(() => false);
  } else {
    // Fallback
    const textArea = document.createElement('textarea');
    textArea.value = shareText;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};

/**
 * Share to WhatsApp with image
 */
export const shareToWhatsApp = async (imageUrl) => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  try {
    // Add watermark first
    const watermarkedBlob = await addWatermark(imageUrl);
    const file = new File([watermarkedBlob], 'fittty-tryon.jpg', { type: 'image/jpeg' });
    
    // Only try Web Share API on mobile devices
    if (isMobile && navigator.share && navigator.canShare) {
      // Check if can share files
      try {
        const canShareFiles = navigator.canShare({ files: [file] });
        
        if (canShareFiles) {
          await navigator.share({
            files: [file],
            title: 'My Virtual Try-On',
            text: 'Check out my virtual try-on from FITTTY! 🎨👔'
          });
          return { success: true, method: 'share-api', message: 'Shared successfully!' };
        }
      } catch (shareError) {
        // User cancelled
        if (shareError.name === 'AbortError') {
          return { success: false, error: 'Share cancelled', cancelled: true };
        }
        console.warn('Share API failed, falling back to download:', shareError);
        // Fall through to download method
      }
    }
    
    // Desktop or fallback: Download and open WhatsApp
    const url = URL.createObjectURL(watermarkedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fittty-whatsapp.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Open WhatsApp after download
    setTimeout(() => {
      const text = encodeURIComponent('Check out my virtual try-on from FITTTY! 🎨👔');
      const whatsappUrl = isMobile 
        ? `whatsapp://send?text=${text}` 
        : `https://web.whatsapp.com/send?text=${text}`;
      
      window.open(whatsappUrl, '_blank');
      
      // Revoke URL after opening
      URL.revokeObjectURL(url);
    }, 500);
    
    return { 
      success: true, 
      method: 'download',
      message: isMobile 
        ? '✅ Image saved! WhatsApp opening - tap 📎 to attach.'
        : '✅ Image downloaded! WhatsApp opening - click 📎 to attach from Downloads.'
    };
    
  } catch (error) {
    console.error('Error sharing to WhatsApp:', error);
    
    // Ultimate fallback - just text link
    const text = encodeURIComponent('Check out my virtual try-on from FITTTY! 🎨👔');
    const whatsappUrl = isMobile 
      ? `whatsapp://send?text=${text}` 
      : `https://web.whatsapp.com/send?text=${text}`;
    
    try {
      window.open(whatsappUrl, '_blank');
    } catch (openError) {
      console.error('Failed to open WhatsApp:', openError);
    }
    
    return { 
      success: false, 
      error: error.message || 'Failed to process image',
      details: 'Opening WhatsApp with text only'
    };
  }
};

/**
 * Share to Facebook
 */
export const shareToFacebook = () => {
  const url = encodeURIComponent(window.location.origin);
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to Twitter/X
 */
export const shareToTwitter = () => {
  const text = encodeURIComponent('Check out my virtual try-on from FITTTY! 🎨👔 #VirtualTryOn #Fashion');
  const url = encodeURIComponent(window.location.origin);
  const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};
